import NewClaimTicketForm from '@Components/claim-flow/standard-claim-flow'
import { ErrorBoundary } from 'react-error-boundary'

import './main-app.scss'
import { RootState, useAppDispatch } from '@AppState/redux-store'
import { useSelector } from 'react-redux'
import { useCallback, useEffect } from 'react'
import { AppConfigLoader } from '@Components/app-config/app-config'
import { GameDataServiceAPI } from '@API/data-services/game-data-service'
import { PlayerDataServiceAPI } from '@API/data-services/player-data-service'
import { DataFetchingState } from '@AppState/types'
import { Route, Routes, useNavigate, useSearchParams } from 'react-router-dom'
import { fetchClaimReservation } from '@AppState/claim-reservation'
import { EditReservationScreen } from '@Components/status-screens/edit-reservation-screen'
import { DeleteReservationScreen } from '@Components/status-screens/delete-reservation-screen'
import { ConfirmationScreen } from '@Components/status-screens/confirmation-screen'
import { LoadingIndicator } from '@Components/loading-indicator/loading-indicator'
import { DamagedTicketScreen } from '@Components/status-screens/damaged-ticket-screen'
import { UnknownWinScreen } from '@Components/status-screens/unknown-win-screen'
import { BigWinScreen } from '@Components/status-screens/big-winner-screen'
import { ErrorPage } from '@Components/status-screens/error-page'
import EditClaimTicketForm from '@Components/claim-flow/edit-claim-flow'
import { ClaimFormFlow } from '@Components/claim-form-flow/claim-form-flow'
import { FLOWS, PATHS, QUERY_PARAMS } from '../../constants'
import { ManyTicketsScreen } from '@Components/status-screens/many-tickets-screen'
import { RegularTicketScreen } from '@Components/status-screens/regular-ticket-screen'

export const MainApp = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appConfig = useSelector((state: RootState) => state.appConfig.config)
  const claimReservationState = useSelector((state: RootState) => state.claimReservation)

  const resDetails = claimReservationState.claimReservation

  const appReady = useCallback(() => {
    const fetchingReservation = claimReservationState.fetchState === DataFetchingState.PENDING
    return appConfig && !fetchingReservation
  }, [appConfig, claimReservationState])
  useEffect(() => {
    if (!appConfig) {
      return
    }

    GameDataServiceAPI.configureApi({
      apiKey: appConfig.gameDataApiKey,
      basePath: appConfig.gameDataServiceUrl,
    })

    PlayerDataServiceAPI.configureApi({
      apiKey: appConfig.playerDataApiKey,
      basePath: appConfig.playerDataServiceUrl,
    })

    const initialClaimID = searchParams.get(QUERY_PARAMS.CLAIM_ID)

    if (initialClaimID) {
      dispatch(fetchClaimReservation(initialClaimID))
    }
  }, [appConfig, dispatch, searchParams])

  useEffect(() => {
    if (location.pathname === '/' && searchParams.get(QUERY_PARAMS.FLOW) === FLOWS.CLAIM_FORM) {
      navigate(`${PATHS.CLAIM_FORM}${location.search}`, { replace: true })
    }
  }, [navigate, searchParams])

  useEffect(() => {
    if (
      claimReservationState.fetchState === DataFetchingState.SUCCEEDED &&
      resDetails !== null &&
      location.pathname === '/' &&
      searchParams.get(QUERY_PARAMS.FLOW) !== FLOWS.CLAIM_FORM
    ) {
      navigate(`${PATHS.EDIT_APPOINTMENT}${location.search}`, { replace: true })
    }
  }, [claimReservationState, navigate, resDetails, searchParams])

  if (!appReady()) {
    return (
      <ErrorBoundary fallback={<ErrorPage />}>
        <AppConfigLoader />
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary fallback={<ErrorPage />}>
      <main className="main-app">
        <Routes>
          <Route
            path={PATHS.CONFIRMATION}
            element={
              resDetails ? (
                <ConfirmationScreen reservation={resDetails} />
              ) : (
                <LoadingIndicator active={true} />
              )
            }
          />
          <Route path={PATHS.NEW_APPOINTMENT} element={<NewClaimTicketForm />} />
          <Route
            path={PATHS.RESCHEDULE_APPOINTMENT}
            element={
              resDetails ? (
                <EditClaimTicketForm originalReservation={resDetails!} />
              ) : (
                <NewClaimTicketForm />
              )
            }
          />
          <Route
            path={PATHS.EDIT_APPOINTMENT}
            element={
              resDetails ? (
                <EditReservationScreen reservation={resDetails} />
              ) : (
                <NewClaimTicketForm />
              )
            }
          />
          <Route
            path={PATHS.DELETE_APPOINTMENT}
            element={
              resDetails ? (
                <DeleteReservationScreen reservation={resDetails} />
              ) : (
                <NewClaimTicketForm />
              )
            }
          />
          <Route path={PATHS.DAMAGED_TICKET} element={<DamagedTicketScreen />} />
          <Route path={PATHS.BIG_WINNER} element={<BigWinScreen />} />
          <Route path={PATHS.REGULAR_TICKET} element={<RegularTicketScreen />} />
          <Route path={PATHS.UNKNOWN} element={<UnknownWinScreen />} />
          <Route path={PATHS.MANY_TICKETS} element={<ManyTicketsScreen />} />
          {appConfig?.enableClaimForm && (
            <Route path={PATHS.CLAIM_FORM} element={<ClaimFormFlow />} />
          )}
          <Route path="*" element={<NewClaimTicketForm />} />
        </Routes>
      </main>
    </ErrorBoundary>
  )
}
